import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { type CoreMessageType, type SendableMessageType } from "@sendbird/uikit-react/types/utils";
import { Button } from "@src/appV2/redesign/components/Button";
import type React from "react";
import { useHistory } from "react-router-dom";

import { PLACEMENT_PATHS } from "../../Placements/paths";
import {
  isChatMessageWithText,
  isSendBirdGroupChannel,
  type PlacementChatMetadata,
} from "../types";
import { PlacementChatMessageLayout } from "./PlacementChatMessageLayout";

function PlacementChatRejectionMessageContent(props: { chatChannel: GroupChannel }) {
  const { chatChannel } = props;
  const { facilityName, hcpName } = chatChannel.cachedMetaData as PlacementChatMetadata;
  const history = useHistory();

  // Deep link path to the jobs feed
  const JOBS_FEED_PATH = PLACEMENT_PATHS.ROOT;

  const defaultMessage = `Hi ${hcpName ?? "there"}. Unfortunately, ${
    facilityName ?? "this facility"
  } has declined your interview request. We encourage you to apply to other jobs! Not every job will be a good fit, but we guarantee you'll hear back within 3 business days for all applications.`;

  const button = (
    <Button
      variant="contained"
      size="small"
      sx={{ mt: 4 }}
      onClick={() => {
        history.push(JOBS_FEED_PATH);
      }}
    >
      View jobs
    </Button>
  );

  // The casting is necessary for type compatibility with Sendbird's uikit Channel.renderMessage prop
  return (
    <PlacementChatMessageLayout message={defaultMessage.trim()} button={button} />
  ) as unknown as React.ReactElement;
}

export function PlacementChatRejectionMessage({
  message,
  channel,
}: {
  message: CoreMessageType;
  channel: unknown;
}) {
  const chatMessage = (
    typeof message === "object" && message !== null
      ? message
      : // FIXME: Automatically disabled during ESLint config update
        JSON.parse(JSON.stringify(message))
  ) as unknown;

  const chatChannel = (
    typeof channel === "object" && channel !== null
      ? channel
      : // FIXME: Automatically disabled during ESLint config update
        JSON.parse(JSON.stringify(channel))
  ) as unknown;

  if (!isChatMessageWithText(chatMessage) || !isSendBirdGroupChannel(chatChannel)) {
    return null as unknown as React.ReactElement;
  }

  if (
    chatMessage.customType === "PLACEMENT_APPLICATION_REJECTION_BY_WORKPLACE" &&
    chatChannel.customType === "placements"
  ) {
    return <PlacementChatRejectionMessageContent chatChannel={chatChannel} />;
  }

  // the message user could be the managing (parent) facility, but this would be confusing for users
  // so we overwrite the name to show the true facility name
  const placementChatMetadata = chatChannel.cachedMetaData as PlacementChatMetadata;
  const { sender } = message as SendableMessageType;
  if (sender && placementChatMetadata.facilityName) {
    sender.nickname = placementChatMetadata.facilityName;
    sender.friendName = placementChatMetadata.facilityName;
  }

  return null as unknown as React.ReactElement;
}
